import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";

const HeatmapChart = ({
  data,
  factor = 1,
  unit = "",
  heightPx = 500,
  param,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    if (!chartRef.current || !data) return;

    if (chartInstance.current) {
      chartInstance.current.dispose();
      chartInstance.current = null;
    }

    const myChart = echarts.init(chartRef.current);
    chartInstance.current = myChart;

    const scaledMatrix = data.matrix.map(([x, y, value]) => [
      x,
      y,
      value != null ? value / factor : value,
    ]);

    const values = scaledMatrix.map((m) => m[2]);
    const maxVal = values.length ? Math.max(...values) : 0;
    const minVal = values.length ? Math.min(...values) : 0;

    const option = {
      animation: false,
      tooltip: {
        position: "top",
        formatter: (params) => {
          const [xIndex, yIndex, value] = params.data;
          return `
            <strong>${data.yAxis[yIndex]}</strong><br/>
            Hora: ${data.xAxis[xIndex]}<br/>
            Valor: ${value.toFixed(2)} ${unit}
          `;
        },
      },
      grid: {
        left: 60,
        right: 20,
        top: 40,
        bottom: 60,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: data.xAxis,
        splitArea: { show: true },
        axisLabel: {
          rotate: 45,
        },
      },
      yAxis: {
        type: "category",
        data: data.yAxis,
        splitArea: { show: true },
      },
      visualMap: {
        min: minVal,
        max: maxVal,
        orient: "horizontal",
        left: "center",
        bottom: 10,
        calculable: true,
        inRange: {
          color: [
            "#1e3a8a",
            "#2563eb",
            "#38bdf8",
            "#22c55e",
            "#facc15",
            "#fb923c",
            "#ef4444",
          ],
        },
        text: ["Alto consumo", "Bajo consumo"],
      },

      series: [
        {
          name: "Consumo",
          type: "heatmap",
          data: scaledMatrix,
          label: { show: false },
          itemStyle: {
            borderWidth: 0.5,
            borderColor: "rgba(255,255,255,0.15)",
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 8,
              shadowColor: "rgba(0,0,0,0.4)",
            },
          },
        },
      ],
    };

    myChart.setOption(option, true);

    const resizeObserver = new ResizeObserver(() => {
      myChart.resize();
    });

    resizeObserver.observe(chartRef.current);

    return () => {
      resizeObserver.disconnect();
      myChart.dispose();
      chartInstance.current = null;
    };
  }, [data]);

  const getParamLabel = (param) => {
    switch (param) {
      case "potencia activa":
        return "Mapa de calor – Potencia Activa";
      case "potencia reactiva":
        return "Mapa de calor – Potencia Reactiva";
      case "energia activa":
        return "Mapa de calor – Energía Activa";
      case "energia reactiva":
        return "Mapa de calor – Energía Reactiva";
      default:
        return "Mapa de calor";
    }
  };

  return (
    <div className="heatmap-container">
      <div className="heatmap-title">
        {getParamLabel(param)} ({unit})
      </div>

      <div
        style={{
          width: "100%",
          height: heightPx,
        }}
        ref={chartRef}
      />
    </div>
  );
};

export default HeatmapChart;
